export enum ContactPreferenceChannel {
  Email = 'email',
  Text = 'text',
}

export enum ContactPreferenceCategory {
  AccountTransactions = 'accountTransactions',
  ChildUpdates = 'childUpdates',
  YourImpact = 'yourImpact',
  Communications = 'communications',
  HelpUsImprove = 'helpUsImprove',
  PrayerNetwork = 'prayerNetwork',
  ExplorerMagazine = 'explorerMagazine',
  VolunteeringVisits = 'volunteeringVisits',
}

export interface PreferenceChannelConfig {
  serverKey: string;
  channels: ContactPreferenceChannel[];
  disabledChannels?: ContactPreferenceChannel[];
  alwaysCheckedChannels?: ContactPreferenceChannel[];
}

/**
 * Server keys absent from this config are omitted from the UI and treated as
 * unmanaged so the payload builder preserves values users cannot see or edit.
 */
export const contactPreferenceChannels: Record<
  ContactPreferenceCategory,
  PreferenceChannelConfig
> = {
  [ContactPreferenceCategory.AccountTransactions]: {
    serverKey: 'Account_Transaction',
    channels: [ContactPreferenceChannel.Email, ContactPreferenceChannel.Text],
    disabledChannels: [ContactPreferenceChannel.Email],
    alwaysCheckedChannels: [ContactPreferenceChannel.Email],
  },
  [ContactPreferenceCategory.ChildUpdates]: {
    serverKey: 'Sponsored_Child_Communication',
    channels: [ContactPreferenceChannel.Email, ContactPreferenceChannel.Text],
  },
  [ContactPreferenceCategory.YourImpact]: {
    serverKey: 'Your_Impact',
    channels: [ContactPreferenceChannel.Email, ContactPreferenceChannel.Text],
  },
  [ContactPreferenceCategory.Communications]: {
    serverKey: 'Compassion_Communication',
    channels: [ContactPreferenceChannel.Email],
  },
  [ContactPreferenceCategory.HelpUsImprove]: {
    serverKey: 'Help_Us_Improve',
    channels: [ContactPreferenceChannel.Email],
  },
  [ContactPreferenceCategory.PrayerNetwork]: {
    serverKey: 'Compassion_Prayer_Network',
    channels: [ContactPreferenceChannel.Email],
  },
  [ContactPreferenceCategory.ExplorerMagazine]: {
    serverKey: 'Compassion_Explorer_Magazine_for_Kids',
    channels: [ContactPreferenceChannel.Email],
  },
  [ContactPreferenceCategory.VolunteeringVisits]: {
    serverKey: 'Volunteering_and_Visits',
    channels: [ContactPreferenceChannel.Email, ContactPreferenceChannel.Text],
  },
};

const serverKeyToCategory = new Map<string, ContactPreferenceCategory>(
  Object.values(ContactPreferenceCategory).map((category) => [
    contactPreferenceChannels[category].serverKey,
    category,
  ]),
);

export const getContactPreferenceCategory = (
  serverKey: string,
): ContactPreferenceCategory | undefined => serverKeyToCategory.get(serverKey);

export const getServerKeyForContactPreferenceCategory = (
  category: ContactPreferenceCategory,
): string => contactPreferenceChannels[category].serverKey;

interface ContactPreferenceChannelState {
  isAlwaysChecked: boolean;
  isDisabled: boolean;
}

const getContactPreferenceChannelState = (
  serverKey: string,
  channel: ContactPreferenceChannel,
): ContactPreferenceChannelState | undefined => {
  const category = serverKeyToCategory.get(serverKey);
  if (!category) {
    return undefined;
  }

  const { channels, disabledChannels, alwaysCheckedChannels } =
    contactPreferenceChannels[category];
  if (!channels.includes(channel)) {
    return undefined;
  }

  return {
    isAlwaysChecked: alwaysCheckedChannels?.includes(channel) ?? false,
    isDisabled: disabledChannels?.includes(channel) ?? false,
  };
};

/**
 * Returns whether the sheet manages a server key on the given channel.
 * Always-checked channels are managed so stale server values can be removed;
 * disabled-only channels are preserved because users cannot change them.
 */
export const isContactPreferenceChannelManaged = (
  serverKey: string,
  channel: ContactPreferenceChannel,
): boolean => {
  const state = getContactPreferenceChannelState(serverKey, channel);
  return state != null && (state.isAlwaysChecked || !state.isDisabled);
};

/**
 * Returns whether a server key represents a user-editable preference on the
 * given channel. Unknown keys and backend-controlled channels do not count as
 * user preferences.
 */
export const isContactPreferenceChannelEditable = (
  serverKey: string,
  channel: ContactPreferenceChannel,
): boolean => {
  const state = getContactPreferenceChannelState(serverKey, channel);
  return state != null && !state.isDisabled && !state.isAlwaysChecked;
};
